import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Avatar } from '@/components/ui/Avatar';
import { Skeleton } from '@/components/ui/Skeleton';
import { FilterPill } from '@/components/common/FilterPill';
import { ErrorState } from '@/components/common/ErrorState';
import { EmptyState } from '@/components/common/EmptyState';
import { Pagination } from '@/components/common/Pagination';
import { useCustomers } from '@/data/api';
import { formatKes, maskPhone, maskEmail } from '@/lib/format';
import { cn } from '@/lib/cn';

const PAGE_SIZE = 20;

/**
 * The legacy function get_account_status_dynamic_name() returns free-form
 * labels — "Active", "Disconnected", "New Account", "Closed" etc. — that we
 * can't enumerate exhaustively. Bucket them into a colour-class based on
 * the substring so the badge stays readable.
 */
function statusBadgeClass(label: string): string {
  const s = (label || '').toLowerCase();
  if (s.includes('active') || s.includes('normal')) {
    return 'bg-status-success-bg text-status-success';
  }
  if (s.includes('disconn') || s.includes('closed') || s.includes('suspend')) {
    return 'bg-status-error-bg text-status-error';
  }
  if (s.includes('new') || s.includes('pending') || s.includes('review')) {
    return 'bg-status-warning-bg text-status-warning-text';
  }
  return 'bg-line-soft text-ink-muted';
}

export function CustomersPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'ACTIVE' | 'SUSPENDED'>('ALL');
  const [page, setPage] = useState(0);

  // Reset to first page whenever filters change so we never land on an
  // out-of-range page after the result set shrinks.
  useEffect(() => {
    setPage(0);
  }, [search, statusFilter]);

  const listQuery = useCustomers({
    search,
    status: statusFilter === 'ALL' ? undefined : statusFilter,
    page,
    size: PAGE_SIZE,
  });
  const filtered = listQuery.data?.content ?? [];
  const totalElements = listQuery.data?.totalElements ?? 0;
  const totalPages = listQuery.data?.totalPages ?? 0;

  return (
    <div className="flex flex-col gap-4">
      <Card padding="sm" className="flex flex-wrap items-center gap-2.5">
        <Input
          containerClassName="w-72"
          placeholder="Search by name, phone, email"
          iconLeft={<Search className="h-4 w-4" />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <FilterPill
          label={statusFilter === 'ALL' ? 'All statuses' : statusFilter === 'ACTIVE' ? 'Active' : 'Suspended'}
          active={statusFilter !== 'ALL'}
          onClick={() => {
            setStatusFilter((s) =>
              s === 'ALL' ? 'ACTIVE' : s === 'ACTIVE' ? 'SUSPENDED' : 'ALL',
            );
          }}
        />
      </Card>

      <Card padding="none" className="overflow-hidden">
        {listQuery.isLoading ? (
          <div className="p-4 space-y-2">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        ) : listQuery.isError ? (
          <ErrorState
            title="Couldn't load customers"
            description={(listQuery.error as Error)?.message ?? 'Try again.'}
            onRetry={() => listQuery.refetch()}
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            title="No customers match your filters"
            description="Adjust the search or status filter."
          />
        ) : (
        <table className="w-full text-body">
          <thead className="border-b border-line">
            <tr className="text-overline text-ink-hint uppercase">
              <th className="text-left px-4 py-2.5">Account No.</th>
              <th className="text-left py-2.5">Account Name</th>
              <th className="text-right py-2.5">Balance</th>
              <th className="text-left py-2.5 pl-6">Status</th>
              <th className="text-left py-2.5">Phone</th>
              <th className="text-left px-4 py-2.5">Email</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr
                key={c.id}
                className="border-t border-line-soft hover:bg-canvas-hover transition-colors"
              >
                <td className="px-4 py-2.5">
                  <Link
                    to={`/customers/${c.id}`}
                    className="font-mono text-caption font-semibold text-brand-blue hover:underline"
                  >
                    {c.accountNumber}
                  </Link>
                </td>
                <td className="py-2.5">
                  <div className="flex items-center gap-2.5">
                    <Avatar name={c.name} size="sm" />
                    <span className="font-semibold text-ink">{c.name}</span>
                  </div>
                </td>
                <td
                  className={cn(
                    'py-2.5 text-right font-mono tabular',
                    c.balance > 0 ? 'text-status-error' : 'text-ink',
                  )}
                >
                  KES {formatKes(c.balance, 2)}
                </td>
                <td className="py-2.5 pl-6">
                  <span
                    className={cn(
                      'inline-flex items-center rounded-pill px-2.5 py-0.5 text-overline uppercase',
                      statusBadgeClass(c.statusLabel),
                    )}
                  >
                    {c.statusLabel}
                  </span>
                </td>
                <td className="py-2.5 font-mono text-caption">{maskPhone(c.phone)}</td>
                <td className="px-4 py-2.5 text-ink-muted">{maskEmail(c.email)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
        {!listQuery.isLoading && !listQuery.isError && totalElements > 0 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            totalElements={totalElements}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        )}
      </Card>
    </div>
  );
}
